import { EditorDOMElements, EditorDOMUtils } from 'Editor/services/_Common/DOM';

import {
  TabBarWidgetModel,
  ImageOptionsWidgetModel,
  ResizableWidgetModel,
  TableOptionsWidgetModel,
  EquationsOptionsWidgetModel,
  OnboardingPulseWidgetModel,
  PasteOptionsWidgetModel,
  TooltipWidgetModel,
  LockTooltipWidgetModel,
} from '../../ViewModels/WidgetViewModels';

type Timeouts = {
  [index in Editor.Visualizer.WidgetTypes]: {
    [index: string]: {
      [index: string]: NodeJS.Timeout;
    };
  };
};

// Widgets are stored in a two-level nested map: [viewId][splitId] -> widget instance.
// This allows the same element (sharing the same id) to have independent widget instances
// per split view when a block element overflows across multiple pages in page layout.
type WidgetSplitMap = {
  [index: string]: Editor.Visualizer.IWidgetViewModel;
};

type DynamicWidgets = {
  [index in Editor.Visualizer.DynamicWidgets]: {
    [index: string]: WidgetSplitMap;
  };
};

type FixedWidgets = {
  [index in Editor.Visualizer.FixedWidgets]: {
    [index: string]: WidgetSplitMap;
  };
};

export class WidgetManager {
  private Data: Editor.Data.API;
  private Visualizer: Editor.Visualizer.State;

  private widgetsContainer?: HTMLElement;

  private timeouts: Timeouts = {
    resizable: {},
    tableOptions: {},
    imageOptions: {},
    tabulations: {},
    onboardingPulse: {},
    equationOptions: {},
    pasteOptions: {},
    tooltip: {},
    lockTooltip: {},
  };

  private dynamicWidgets: DynamicWidgets = {
    resizable: {},
    tableOptions: {},
    imageOptions: {},
    equationOptions: {},
    pasteOptions: {},
    tooltip: {},
    lockTooltip: {},
  };

  private fixedWidgets: FixedWidgets = {
    tabulations: {},
    onboardingPulse: {},
  };

  private debug: boolean = false;

  private applyLayoutContainerStyle(layout: Editor.Visualizer.LayoutType) {
    if (!this.widgetsContainer) {
      return;
    }

    switch (layout) {
      case 'PAGE':
        this.widgetsContainer.style.left = '50%';
        this.widgetsContainer.style.marginLeft = '0px';
        this.widgetsContainer.style.marginRight = '0px';
        break;
      case 'WEB':
        this.widgetsContainer.style.left = '0px';
        this.widgetsContainer.style.marginLeft = '88px';
        this.widgetsContainer.style.marginRight = '88px';
        break;
      default:
        break;
    }
  }

  constructor(dataManager: Editor.Data.API, Visualizer: Editor.Visualizer.State) {
    this.Data = dataManager;
    this.Visualizer = Visualizer;

    this.scheduleAddWidget = this.scheduleAddWidget.bind(this);
    this.addWidget = this.addWidget.bind(this);

    this.rebuildWidgets = this.rebuildWidgets.bind(this);
    this.rebuildWidgetForView = this.rebuildWidgetForView.bind(this);

    this.handleImageWrapChange = this.handleImageWrapChange.bind(this);
  }

  get widgetsDOMContainer() {
    return this.widgetsContainer!;
  }

  start() {
    if (this.debug) {
      logger.trace('WidgetManager start');
    }
    const editorContainer = document.getElementById('EditorContainer');
    if (editorContainer) {
      this.widgetsContainer = document.createElement('div');
      this.widgetsContainer.id = 'widgetContainer';
      this.widgetsContainer.style.position = 'absolute';
      this.widgetsContainer.style.top = '0px';
      this.widgetsContainer.style.left = '0px';
      this.widgetsContainer.style.zIndex = '1';
      this.widgetsContainer.style.marginLeft = '88px';
      this.widgetsContainer.style.marginRight = '88px';

      editorContainer.appendChild(this.widgetsContainer);

      // Set container position for the current layout on first render without forcing a rebuild
      this.applyLayoutContainerStyle(this.Visualizer.layoutType);
    }

    if (this.Visualizer?.hooks?.afterAllParagraphStylesUpdate) {
      this.Visualizer.hooks.afterAllParagraphStylesUpdate?.register(() => {
        setTimeout(() => {
          this.rebuildWidgets();
        }, 0);
      });
    }
  }

  destroy() {
    if (this.debug) {
      logger.trace('WidgetManager destroy');
    }
    this.removeAllWidgets();

    if (this.widgetsContainer) {
      this.widgetsContainer.remove();
      delete this.widgetsContainer;
    }
  }

  isFixedWidget(type: Editor.Visualizer.WidgetTypes): type is Editor.Visualizer.FixedWidgets {
    return type === 'tabulations' || type === 'onboardingPulse';
  }

  layoutChanged(layout: Editor.Visualizer.LayoutType) {
    if (this.debug) {
      logger.trace('WidgetManager layoutChanged ' + layout);
    }
    this.removeAllWidgets();

    this.applyLayoutContainerStyle(layout);
  }

  private handleImageWrapChange(props: Editor.Edition.ImageProperties) {
    this.Visualizer.editionManager?.handleUpdateImageProperties(props);
  }

  private getOrCreateWidgetSplitMap(
    widgets: Record<string, WidgetSplitMap>,
    viewId: string,
  ): WidgetSplitMap {
    if (!widgets[viewId]) {
      widgets[viewId] = {};
    }

    return widgets[viewId];
  }

  private getWidgetSplitIds(
    widgets: Record<string, WidgetSplitMap>,
    viewId: string,
    splitId?: string,
  ) {
    const viewWidgets = widgets[viewId];

    if (!viewWidgets) {
      return [];
    }

    // If a specific splitId is requested, return only that one (or empty if not found)
    if (splitId != null) {
      return viewWidgets[splitId] ? [splitId] : [];
    }

    // Otherwise return all splitIds for this viewId
    return Object.keys(viewWidgets);
  }

  private cleanupWidgetSplitMap(widgets: Record<string, WidgetSplitMap>, viewId: string) {
    if (widgets[viewId] && Object.keys(widgets[viewId]).length === 0) {
      delete widgets[viewId];
    }
  }

  private rebuildMatchingWidgets(
    widgets: Record<string, WidgetSplitMap>,
    viewId: string,
    docContainer: HTMLElement | Node | null,
  ) {
    const splitIds = this.getWidgetSplitIds(widgets, viewId);
    const viewWidgets = widgets[viewId];

    for (let index = 0; index < splitIds.length; index++) {
      const splitId = splitIds[index];
      const widget = viewWidgets?.[splitId];

      if (!widget) {
        continue;
      }

      if (docContainer?.contains(widget.getRefView())) {
        widget.render();
      } else {
        widget.dispose();
        delete viewWidgets[splitId];
      }
    }

    this.cleanupWidgetSplitMap(widgets, viewId);
  }

  private removeMatchingWidgets(
    widgets: Record<string, WidgetSplitMap>,
    viewId: string,
    splitId?: string,
  ) {
    const splitIds = this.getWidgetSplitIds(widgets, viewId, splitId);
    const viewWidgets = widgets[viewId];

    for (let index = 0; index < splitIds.length; index++) {
      const currentSplitId = splitIds[index];
      const widget = viewWidgets?.[currentSplitId];

      if (!widget) {
        continue;
      }

      widget.dispose();
      delete viewWidgets[currentSplitId];
    }

    this.cleanupWidgetSplitMap(widgets, viewId);
  }

  private hasMatchingWidgets(widgets: Record<string, WidgetSplitMap>, viewId: string): boolean {
    return this.getWidgetSplitIds(widgets, viewId).length > 0;
  }

  private addEquationOptionsWidget(
    view: Editor.Visualizer.WidgetDataMapper['equationOptions']['view'],
    props: Editor.Visualizer.WidgetDataMapper['equationOptions']['props'],
  ) {
    if (!this.widgetsContainer || !EditorDOMElements.isEquationElement(view)) {
      return;
    }

    const propsToWidget = {
      ...props,
    };

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.dynamicWidgets['equationOptions'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new EquationsOptionsWidgetModel(this.Visualizer, view, propsToWidget);

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(propsToWidget);
    }

    widget.render();
  }

  private addImageOptionsWidget(
    view: Editor.Visualizer.WidgetDataMapper['imageOptions']['view'],
    props: Editor.Visualizer.WidgetDataMapper['imageOptions']['props'],
  ) {
    if (EditorDOMElements.isFigureElement(view) && view.firstElementChild != null) {
      view = view.firstElementChild as Editor.Elements.ImageElement;
    }

    if (!this.widgetsContainer || !EditorDOMElements.isImageElement(view)) {
      return;
    }

    const propsToWidget = {
      ...props,
      onImageWrapChange: this.handleImageWrapChange,
    };

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.dynamicWidgets['imageOptions'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new ImageOptionsWidgetModel(this.Visualizer, view, propsToWidget);

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(propsToWidget);
    }

    widget.render();
  }

  private addTableOptionsWidget(
    view: Editor.Visualizer.WidgetDataMapper['tableOptions']['view'],
    props: Editor.Visualizer.WidgetDataMapper['tableOptions']['props'],
  ) {
    if (!this.widgetsContainer || !EditorDOMElements.isTableElement(view)) {
      return;
    }

    const propsToWidget = {
      ...props,
    };

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.dynamicWidgets['tableOptions'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      // build widget view and append it to widgets container
      widget = new TableOptionsWidgetModel(this.Visualizer, view, propsToWidget);

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(propsToWidget);
    }

    widget.render();
  }

  private addResizableWidget(
    view: Editor.Visualizer.WidgetDataMapper['resizable']['view'],
    props: Editor.Visualizer.WidgetDataMapper['resizable']['props'],
  ) {
    if (EditorDOMElements.isFigureElement(view) && view.firstElementChild != null) {
      view = view.firstElementChild as Editor.Elements.ImageElement;
    }

    if (!this.widgetsContainer) {
      return;
    }

    if (EditorDOMElements.isImageElement(view) || EditorDOMElements.isTableElement(view)) {
      const splitId = view.getSplitId();
      const widgetsByView = this.getOrCreateWidgetSplitMap(
        this.dynamicWidgets['resizable'],
        view.id,
      );
      let widget = widgetsByView[splitId];
      if (!widget) {
        widget = new ResizableWidgetModel(this.Visualizer, view, props);

        widgetsByView[splitId] = widget;
        this.widgetsContainer.appendChild(widget.getContainer());
      } else {
        widget.updateProps(props);
      }

      widget.render();
    }
  }

  private addTabBarWidget(
    view: Editor.Visualizer.WidgetDataMapper['tabulations']['view'],
    props: Editor.Visualizer.WidgetDataMapper['tabulations']['props'],
  ) {
    if (!EditorDOMElements.isParagraphElement(view) || !this.widgetsContainer) {
      return;
    }

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(this.fixedWidgets['tabulations'], view.id);
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new TabBarWidgetModel(this.Visualizer, view, {
        ...props,
      });

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(props);
    }

    widget.render();
  }

  private addOnboardingPulseWidget(
    view: Editor.Visualizer.WidgetDataMapper['onboardingPulse']['view'],
    props: Editor.Visualizer.WidgetDataMapper['onboardingPulse']['props'],
  ) {
    if (!EditorDOMElements.isParagraphElement(view) || !this.widgetsContainer) {
      return;
    }

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.fixedWidgets['onboardingPulse'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new OnboardingPulseWidgetModel(this.Visualizer, view, {
        ...props,
      });

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(props);
    }

    widget.render();
  }

  private addPasteOptionsWidget(
    view: Editor.Visualizer.WidgetDataMapper['pasteOptions']['view'],
    props: Editor.Visualizer.WidgetDataMapper['pasteOptions']['props'],
  ) {
    if (!this.widgetsContainer) {
      return;
    }

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.dynamicWidgets['pasteOptions'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new PasteOptionsWidgetModel(this.Visualizer, view, {
        ...props,
      });

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(props);
    }

    widget.render();
  }

  private addTooltipWidget(
    view: Editor.Visualizer.WidgetDataMapper['tooltip']['view'],
    props: Editor.Visualizer.WidgetDataMapper['tooltip']['props'],
  ) {
    if (!this.widgetsContainer) {
      return;
    }

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(this.dynamicWidgets['tooltip'], view.id);
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new TooltipWidgetModel(this.Visualizer, view, {
        ...props,
      });

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(props);
    }

    widget.render();
  }

  private addLockTooltipWidget(
    view: Editor.Visualizer.WidgetDataMapper['lockTooltip']['view'],
    props: Editor.Visualizer.WidgetDataMapper['lockTooltip']['props'],
  ) {
    if (!this.widgetsContainer) {
      return;
    }

    const splitId = view.getSplitId();
    const widgetsByView = this.getOrCreateWidgetSplitMap(
      this.dynamicWidgets['lockTooltip'],
      view.id,
    );
    let widget = widgetsByView[splitId];
    if (!widget) {
      widget = new LockTooltipWidgetModel(this.Visualizer, view, {
        ...props,
      });

      widgetsByView[splitId] = widget;
      this.widgetsContainer.appendChild(widget.getContainer());
    } else {
      widget.updateProps(props);
    }

    widget.render();
  }

  rebuildWidgets() {
    if (this.debug) {
      logger.trace('WidgetManager rebuildWidgets');
    }

    const docContainer = this.Visualizer.rootContainer || EditorDOMUtils.getContentContainer();

    const widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      const viewIds = Object.keys(this.dynamicWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.rebuildMatchingWidgets(this.dynamicWidgets[type], viewIds[w], docContainer);
      }
    }

    const fixedWidgetTypes = Object.keys(this.fixedWidgets);
    for (let t = 0; t < fixedWidgetTypes.length; t++) {
      const type = fixedWidgetTypes[t] as Editor.Visualizer.FixedWidgets;

      const viewIds = Object.keys(this.fixedWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.rebuildMatchingWidgets(this.fixedWidgets[type], viewIds[w], docContainer);
      }
    }
  }

  rebuildWidgetForView(viewId: string) {
    if (this.debug) {
      logger.trace('WidgetManager rebuildWidgetForView ' + viewId);
    }

    const docContainer = this.Visualizer.rootContainer || EditorDOMUtils.getContentContainer();

    let widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      this.rebuildMatchingWidgets(this.dynamicWidgets[type], viewId, docContainer);
    }

    widgetTypes = Object.keys(this.fixedWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.FixedWidgets;

      this.rebuildMatchingWidgets(this.fixedWidgets[type], viewId, docContainer);
    }
  }

  isAnyWidgetOpenForView(viewId: string) {
    const widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      if (this.hasMatchingWidgets(this.dynamicWidgets[type], viewId)) {
        return true;
      }
    }

    const fixedWidgetTypes = Object.keys(this.fixedWidgets);
    for (let t = 0; t < fixedWidgetTypes.length; t++) {
      const type = fixedWidgetTypes[t] as Editor.Visualizer.FixedWidgets;

      if (this.hasMatchingWidgets(this.fixedWidgets[type], viewId)) {
        return true;
      }
    }
  }

  isWidgetOpen(type: Editor.Visualizer.WidgetTypes, viewId: string) {
    if (this.isFixedWidget(type)) {
      return this.hasMatchingWidgets(this.fixedWidgets[type], viewId);
    } else {
      return this.hasMatchingWidgets(this.dynamicWidgets[type], viewId);
    }
  }

  scheduleAddWidget(...args: Editor.Visualizer.AddWidgetArgs) {
    const [type, view] = args;
    const splitId = view.getSplitId();

    const timeoutsByView = this.timeouts[type][view.id] || {};
    this.timeouts[type][view.id] = timeoutsByView;

    if (timeoutsByView[splitId]) {
      clearTimeout(timeoutsByView[splitId]);
      delete timeoutsByView[splitId];
    }

    timeoutsByView[splitId] = setTimeout(() => {
      this.addWidget(...args);
    }, 50);
  }

  private canAddWidget<T extends Editor.Visualizer.WidgetTypes>(type: T, view: HTMLElement) {
    switch (type) {
      case 'resizable':
      case 'tableOptions':
      case 'equationOptions':
      case 'pasteOptions':
      case 'imageOptions': {
        let userHasPermission = false;

        let elementToCheck: Editor.Visualizer.BaseView = view;
        const containerNode = EditorDOMUtils.getContentContainer(view);

        if (elementToCheck.parentNode !== containerNode) {
          elementToCheck = EditorDOMUtils.findFirstLevelChildNode(
            containerNode,
            elementToCheck,
          ) as Editor.Visualizer.BaseView;
        }

        userHasPermission = this.Data.permissions.canUserPerform(elementToCheck.id, 'edit');

        return EditorDOMUtils.isClosestBlockNodeEditable(view) && userHasPermission;
      }

      case 'tabulations':
      case 'onboardingPulse':
      case 'tooltip':
      case 'lockTooltip':
        return true;

      default:
        return;
    }
  }

  addWidget(...[type, view, props]: Editor.Visualizer.AddWidgetArgs) {
    if (this.canAddWidget(type, view)) {
      if (this.debug) {
        logger.trace('WidgetManager addWidget ' + type, props);
      }

      switch (type) {
        case 'resizable':
          return this.addResizableWidget(view, props);
        case 'tableOptions':
          return this.addTableOptionsWidget(view, props);
        case 'imageOptions':
          return this.addImageOptionsWidget(view, props);
        case 'equationOptions':
          return this.addEquationOptionsWidget(view, props);
        case 'tabulations':
          return this.addTabBarWidget(view, props);
        case 'onboardingPulse':
          return this.addOnboardingPulseWidget(view, props);
        case 'pasteOptions':
          return this.addPasteOptionsWidget(view, props);
        case 'tooltip':
          return this.addTooltipWidget(view, props);
        case 'lockTooltip':
          return this.addLockTooltipWidget(view, props);
        default:
          return;
      }
    }
  }

  removeWidget(type: Editor.Visualizer.WidgetTypes, viewId: string, splitId?: string) {
    if (this.debug) {
      logger.trace('WidgetManager removeWidget ' + type + ' ' + viewId);
    }
    // If splitId is provided, only remove the widget for that specific split;
    // otherwise remove all splits for this viewId
    if (this.isFixedWidget(type)) {
      this.removeMatchingWidgets(this.fixedWidgets[type], viewId, splitId);
    } else {
      this.removeMatchingWidgets(this.dynamicWidgets[type], viewId, splitId);
    }
  }

  removeAllWidgetsForType(type: Editor.Visualizer.WidgetTypes) {
    if (this.debug) {
      logger.trace('WidgetManager removeAllWidgetsForType ' + type);
    }
    if (this.isFixedWidget(type)) {
      const viewIds = Object.keys(this.fixedWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.removeMatchingWidgets(this.fixedWidgets[type], viewIds[w]);
      }
    } else {
      const viewIds = Object.keys(this.dynamicWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.removeMatchingWidgets(this.dynamicWidgets[type], viewIds[w]);
      }
    }
  }

  removeAllWidgetsForView(viewId: string) {
    if (this.debug) {
      logger.trace('WidgetManager removeAllWidgetsForView ' + viewId);
    }
    let widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      this.removeMatchingWidgets(this.dynamicWidgets[type], viewId);
    }

    widgetTypes = Object.keys(this.fixedWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.FixedWidgets;

      this.removeMatchingWidgets(this.fixedWidgets[type], viewId);
    }
  }

  removeAllDynamicWidgets() {
    if (this.debug) {
      logger.trace('WidgetManager removeAllDynamicWidgets');
    }
    let widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      const viewIds = Object.keys(this.dynamicWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.removeMatchingWidgets(this.dynamicWidgets[type], viewIds[w]);
      }
    }
  }

  removeAllFixedWidgets() {
    if (this.debug) {
      logger.trace('WidgetManager removeAllFixedWidgets');
    }
    let widgetTypes = Object.keys(this.fixedWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.FixedWidgets;

      const viewIds = Object.keys(this.fixedWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        this.removeMatchingWidgets(this.fixedWidgets[type], viewIds[w]);
      }
    }
  }

  removeAllWidgets() {
    if (this.debug) {
      logger.trace('WidgetManager removeAllWidgets');
    }
    this.removeAllDynamicWidgets();
    this.removeAllFixedWidgets();
  }

  hideDynamicWidgets() {
    if (this.debug) {
      logger.trace('WidgetManager hideDynamicWidgets');
    }
    let widgetTypes = Object.keys(this.dynamicWidgets);
    for (let t = 0; t < widgetTypes.length; t++) {
      const type = widgetTypes[t] as Editor.Visualizer.DynamicWidgets;

      const viewIds = Object.keys(this.dynamicWidgets[type]);
      for (let w = 0; w < viewIds.length; w++) {
        const splitIds = Object.keys(this.dynamicWidgets[type][viewIds[w]]);

        for (let s = 0; s < splitIds.length; s++) {
          const widget = this.dynamicWidgets[type][viewIds[w]]?.[splitIds[s]];

          if (widget) {
            widget.hide();
          }
        }
      }
    }
  }
}
